import './DownloadDataButton.scss'
import {Alert, Button, CircularProgress, Tooltip} from "@mui/material";
import {Done, GetApp} from "@mui/icons-material";
import React, {useEffect, useState} from "react";
import {cap} from '../../utils/js-utils';
import moment from "moment/moment";

/**
 * Currently, there is no maximum size for the download.
 *
 * Proposal: 20000 -> 1075 seconds. This would be jst enough to not be able to download full dataset
 */
export const MAX_SIZE_DOWNLOAD_SIZE: number | undefined = undefined;

/**
 * If the download size is larger than this, a warning will be shown.
 *
 * 2500 -> 156 seconds
 */
export const WARNING_DOWNLOAD_SIZE: number | undefined = 2500;

const MIN_DURATION_S = 30;
const CRUDE_EST_BASE = 24;
const CRUDE_EST_FACTOR = 0.052;
const CRUDE_EST_OVERSHOOT = 0.01;

/**
 * Get a crude estimate of the duration of the download basec on CAT-2557
 * @param dataSize
 */
function getCrudeEstimate(dataSize: number): number {
    return cap((CRUDE_EST_BASE + CRUDE_EST_FACTOR * dataSize) * (1 + CRUDE_EST_OVERSHOOT), MIN_DURATION_S)
}


type Props = {
    dataLoading: boolean
    dataSize: number | null | undefined
    onClick: () => Promise<void>
    compact?: boolean
}
export const DownloadDataButton: React.FC<Props> = ({dataLoading, dataSize, onClick, compact}) => {
    const outOfRange = dataSize && MAX_SIZE_DOWNLOAD_SIZE !== undefined && dataSize > MAX_SIZE_DOWNLOAD_SIZE;
    const inWarningRange = dataSize && WARNING_DOWNLOAD_SIZE !== undefined && dataSize > WARNING_DOWNLOAD_SIZE;
    const isEmpty = dataSize === null || dataSize === 0;
    const expDurationSeconds = dataSize ? getCrudeEstimate(dataSize) : undefined;

    const [hasDownloadedExcel, setHasDownloadedExcel] = useState(false);
    const [startTime, setStartTime] = useState<Date | null>(null);
    const [tick, setTick] = React.useState<number>(0);
    const isDownloadingExcel = startTime !== null;

    useEffect(() => {
        if (startTime === null) return;
        const intervalId = setInterval(() =>
                // Triggers re-render
                setTick(tick => tick + 1),
            1000,
        );
        return () => clearInterval(intervalId);
         
    }, [tick, startTime]);

    const now = new Date();
    const estCompletionTime = expDurationSeconds
        ? (
            startTime
                ? moment(now).to(moment(startTime).add(expDurationSeconds, 'seconds'))
                : moment(now).to(moment(now).add(expDurationSeconds, 'seconds'))

        )
        : '';

    const disabled = isDownloadingExcel || dataLoading || outOfRange || isEmpty;
    const button = <Button
        color="primary"
        variant="outlined"
        size={compact ? 'small' : 'medium'}
        className={'btn-with-circular-progress button-spacing download-data-button ' + (compact ? 'compact' : '')}
        disabled={disabled}
        startIcon={
            compact ? undefined
                : isDownloadingExcel
                    ? <CircularProgress/>
                    : hasDownloadedExcel
                        ? <Done/>
                        : <GetApp/>
        }
        onClick={() => {
            if (disabled) return;
            setStartTime(new Date())
            onClick()
                .then(() => {
                    setHasDownloadedExcel(true)
                    setTimeout(() => {
                        setHasDownloadedExcel(false)
                    }, 10 * 1000)
                })
                .finally(() => {
                    setStartTime(null)
                })
        }}
        sx={{
            minWidth: compact ? undefined : '16em', // Depends on largest text
        }}>
        {compact
            ? isDownloadingExcel
                ? <CircularProgress/>
                : hasDownloadedExcel
                    ? <Done/>
                    : <GetApp/>
            : isDownloadingExcel
                ? 'Preparing data'
                : 'Download Excel File'
        }
    </Button>;
    return <Tooltip
        className="download-data-button-tooltip"
        slotProps={{popper: {className: 'download-data-button-popper ' + (!isDownloadingExcel && !dataLoading && !outOfRange && inWarningRange ? 'warning' : '')}}}
        title={
            isDownloadingExcel
                ? 'Data extraction is in progress. Estimated completion: ' + estCompletionTime
                : dataLoading
                    ? 'Download is disabled while the dashboard is refreshing. Please wait for the refresh to complete.'
                    : outOfRange ? (
                            'Download unavailable due to large data selection. ' +
                            'Reduce the size with filters to enable the download.'
                        )
                        : inWarningRange ?
                            <Alert severity="warning">
                                A large amount of data is going to be processed.
                                You can apply filters to reduce the size.
                                Estimated completion: {estCompletionTime}
                            </Alert>
                            : isEmpty ? 'No data selected'
                                : (compact
                                    ? ('Download table, estimated completion time: ' + estCompletionTime)
                                    : ('Estimated completion: ' + estCompletionTime))
        }>
        <span>
            {button}
        </span>
    </Tooltip>
}
